// ================================================================
// Voice-Targets — der Assistent SIEHT die tippbaren Knöpfe und tippt sie
// ================================================================
// Bisher brauchte jeder Knopf, den der Assistent drücken können sollte, ein
// EIGENES Backend-Tool («open_view», «open_settings», …). Dreimal an einem
// Tag fehlte dabei ein Wort im Vokabular (accepted, documents, settings) —
// der Assistent KONNTE den Wunsch schlicht nicht ausdrücken.
//
// Neues Muster: die App meldet dem Assistenten bei jeder Nachricht, welche
// markierten Elemente GERADE SICHTBAR sind (collectVoiceTargets, geht als
// ui_context.voice_targets mit). Zwei generische Tools genügen dann:
//   list_ui_targets — «was gibt es auf diesem Bildschirm?»
//   tap_target      — sichtbar hinfahren (Geister-Zeiger) und anklicken
//
// Ein neuer Knopf braucht damit KEIN neues Tool mehr — nur das Attribut
// `data-voice-target="name"` (oder `data-nav`, das die Handy-Navigation
// ohnehin trägt). Das Attribut ist zugleich die Positivliste: der Assistent
// kann NUR antippen, was ein Entwickler ausdrücklich markiert hat, und ein
// Element, das der Nutzer mangels Berechtigung nicht sieht, existiert im
// DOM gar nicht erst. Die Server-Permissions der dahinterliegenden Endpunkte
// bleiben unberührt — der Klick ist exakt der Klick des Nutzers.

import { onUiCommand } from './uiCommands.ts';
import { ghostTap } from './ghostPointer.js';

// Nicht mehr melden — mehr Einträge blähen nur den Prompt jeder Nachricht.
const MAX_TARGETS = 40;
const MAX_LABEL = 60;

/** Sichtbar im Sinne von «kann angetippt werden»? */
function isVisible(el) {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  // display:none-Eltern geben ebenfalls eine Nullfläche — der Rect-Test oben
  // fängt das schon. visibility:hidden nicht, deshalb einmal nachfragen.
  return getComputedStyle(el).visibility !== 'hidden';
}

/** Menschlicher Name des Ziels — fürs Modell, damit es weiss, WAS es antippt.
 *  `data-voice-label` gewinnt: bei Listen-Karten wäre textContent ein Brei aus
 *  Titel, Betrag, Badges und Zeitangabe — das Label trägt dann nur den Namen,
 *  auf den sich der Nutzer bezieht («روی مجید بزن»). */
function labelOf(el) {
  const eigen = el.getAttribute('data-voice-label');
  const aria = el.getAttribute('aria-label');
  const title = el.getAttribute('title');
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  return (eigen || aria || title || text || '').slice(0, MAX_LABEL);
}

/** Ist das Ziel gerade AKTIV/ausgewählt (offener Tab, gewählter Filter)?
 *  Liest die Marker, die die App ohnehin setzt: aria-selected / aria-current
 *  (a11y-Standard) und die im Projekt üblichen Aktiv-Klassen ('on', 'active',
 *  'selected' — z.B. lm-seg-Filter). Ohne Marker gilt: nicht ausgewählt. */
function isSelected(el) {
  if (el.getAttribute('aria-selected') === 'true') return true;
  const cur = el.getAttribute('aria-current');
  if (cur && cur !== 'false') return true;
  const cl = el.classList;
  return cl.contains('on') || cl.contains('active') || cl.contains('selected');
}

/**
 * Alle gerade sichtbaren, markierten Ziele einsammeln.
 * Geht mit JEDER Assistenten-Nachricht als `ui_context.voice_targets` mit —
 * so beschreibt die Liste immer den Bildschirm, den der Nutzer JETZT sieht,
 * ohne dass es einen zweiten Rundlauf bräuchte.
 * `selected` sagt dem Modell, WO der Nutzer gerade IST (offener Tab) — sonst
 * schlug es vor, den Tab zu öffnen, in dem man längst stand.
 */
export function collectVoiceTargets() {
  const seen = new Set();
  const out = [];
  for (const el of document.querySelectorAll('[data-voice-target], [data-nav]')) {
    if (out.length >= MAX_TARGETS) break;
    const name = el.getAttribute('data-voice-target') || el.getAttribute('data-nav');
    if (!name || seen.has(name)) continue;
    if (!isVisible(el)) continue;
    seen.add(name);
    const t = { name, label: labelOf(el) };
    if (isSelected(el)) t.selected = true;
    out.push(t);
  }
  return out;
}

/** Das Element zu einem Ziel-Namen — beide Attribute, nur sichtbare. */
function findTarget(name) {
  const esc = window.CSS?.escape ? CSS.escape(name) : name.replace(/["\\]/g, '');
  for (const el of document.querySelectorAll(`[data-voice-target="${esc}"], [data-nav="${esc}"]`)) {
    if (isVisible(el)) return el;
  }
  return null;
}

/**
 * Handler für TAP_TARGET registrieren — einmal, beim Mounten der
 * AssistantBar (dort, wo auch SCROLL wohnt: shell-agnostisch, der Klick
 * funktioniert in der Handy-Shell wie im Dashboard).
 *
 * Ablauf bewusst «erst zeigen, dann tun»: der Geister-Zeiger fährt hin und
 * tippt an, DANACH läuft der echte Klick — Bild und Wirkung passen zusammen.
 * el.click() löst ein natives Klick-Ereignis aus; React (delegierte Listener)
 * und die Vanilla-Delegation ([data-action]) behandeln es wie einen Finger.
 */
export function installTapTargetHandler() {
  return onUiCommand('TAP_TARGET', (cmd) => {
    const el = findTarget(String(cmd.name || ''));
    if (!el) return false; // ehrlich «nicht da» statt ins Leere zu tippen
    // Listen-Einträge stehen oft UNTERHALB des sichtbaren Ausschnitts (die
    // Liste ist gerendert, der Nutzer hat nur nicht gescrollt). Erst ins Bild
    // holen, kurz warten, DANN tippen — sonst fliegt der Zeiger aus dem
    // Bildschirm und der Nutzer sieht einen Klick ins Nichts.
    const r = el.getBoundingClientRect();
    const imBild = r.top >= 0 && r.bottom <= window.innerHeight;
    if (!imBild) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setTimeout(() => { void ghostTap(el).then(() => el.click()); }, imBild ? 0 : 380);
    return true;
  });
}
